import React, { useEffect, useMemo, useState } from 'react';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import AdminNoteThread from '../../components/admin/AdminNoteThread';
import { displayCategoryForCode } from '../../lib/factoryCategories';
import {
  fetchCampaignRows, markPriorContactBulk, sendClaimInvites, sendFactoryClaim,
  summarizeOutcomes, hasConsent, isSelectable, isFollowUpSelectable, outcomeLabel,
  CAMPAIGN_STATUSES, STATUS_LABEL, OUTCOME_LABEL, SEND_OUTCOME_LABEL, DAILY_SOFT_CAP,
  FOLLOW_UP_OUTCOME_LABEL, CLAIM_INVITE_TEMPLATE, FACTORY_CLAIM_TEMPLATE, MARKETING_RATE_SAR,
} from '../../lib/claimCampaign';

// The claim-invitation campaign board: one row per ACTIVE factory, answering
// "where does this factory stand?" at a glance.
//
// Status is never set here and never computed here — admin_claim_campaign_rows
// derives all five on the server, because the sender applies the same rules and
// a screen that disagreed with it about who is eligible would be worse than no
// screen. This file only renders what the RPC returns.
//
// Separate from /admin2/factory-contacts on purpose: that page answers a
// different question (is the EMAIL usable?), over a different audience (every
// row, hidden ones included), from a plain table select. Folding a third
// orthogonal axis into its Ready / Needs-attention tabs would break it.

const TONE = {
  ok:     { fg: '#2D6A4F', bg: 'rgba(45,106,79,0.10)',   bd: 'rgba(45,106,79,0.28)' },
  bad:    { fg: '#c0392b', bg: 'rgba(192,57,43,0.10)',   bd: 'rgba(192,57,43,0.30)' },
  info:   { fg: '#1F5673', bg: 'rgba(31,86,115,0.10)',   bd: 'rgba(31,86,115,0.28)' },
  action: { fg: '#8B6B3D', bg: 'rgba(154,123,79,0.12)',  bd: 'rgba(154,123,79,0.30)' },
  muted:  { fg: '#6b6560', bg: 'rgba(127,127,127,0.10)', bd: 'rgba(127,127,127,0.25)' },
};

const tdS = { padding: '10px 12px', verticalAlign: 'top' };

// WhatsApp's own palette and font stack, used ONLY by the message preview.
// Hard-coded rather than themed on purpose: the preview is a picture of a phone
// screen, so it has to look the same whatever theme the console is in. These are
// not console tokens and must not be reused as if they were.
const WA = {
  chat:    '#efeae2',
  bubble:  '#ffffff',
  sender:  '#128c7e',
  text:    '#111111',
  footer:  '#8696a0',
  stamp:   '#667781',
  divider: '#e9edef',
  action:  '#027eb5',
  header:  '#008069',
  avatar:  '#00a884',
  bezel:   '#111b21',
  frame:   '#d1d7db',
  // Noto Color Emoji is listed FIRST among the emoji families so it wins where
  // it is installed. It is not loaded here: the project self-hosts no fonts —
  // its only @font-face is a local() reference (index.css:9) — so shipping it
  // would mean editing index.css or index.html, i.e. global styles. On Windows
  // the 🇸🇦 therefore still renders as "SA", because Segoe UI Emoji draws flags
  // as letter pairs by design. That is a console-only artefact: the factory's
  // phone shows the flag.
  font:    'system-ui, -apple-system, "Segoe UI", "Helvetica Neue", Roboto, '
           + '"Noto Color Emoji", "Apple Color Emoji", "Segoe UI Emoji", sans-serif',
  // WhatsApp's chat doodle, as an inline data-URI so nothing is fetched.
  doodle:  'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' '
           + 'width=\'64\' height=\'64\' viewBox=\'0 0 64 64\'%3E%3Cg fill=\'none\' '
           + 'stroke=\'%23000\' stroke-opacity=\'.045\' stroke-width=\'1.4\' '
           + 'stroke-linecap=\'round\'%3E%3Ccircle cx=\'13\' cy=\'13\' r=\'5\'/%3E'
           + '%3Cpath d=\'M34 9h9v9\'/%3E%3Cpath d=\'M7 42q8-9 16 0\'/%3E'
           + '%3Cpath d=\'M42 36l7 7-7 7\'/%3E%3Ccircle cx=\'51\' cy=\'17\' r=\'3\'/%3E'
           + '%3Cpath d=\'M28 52h11\'/%3E%3C/g%3E%3C/svg%3E")',
};

const fmtDate = (iso, isAr) => (iso
  ? new Date(iso).toLocaleDateString(isAr ? 'ar' : 'en', { day: 'numeric', month: 'short', year: 'numeric' })
  : null);

function StatusBadge({ status, isAr }) {
  const meta = STATUS_LABEL[status];
  if (!meta) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  const t = TONE[meta.tone];
  return (
    <span style={{
      display: 'inline-block', whiteSpace: 'nowrap', fontSize: 11, fontWeight: 600,
      color: t.fg, background: t.bg, border: `1px solid ${t.bd}`, borderRadius: 999, padding: '2px 9px',
    }}>{isAr ? meta.ar : meta.en}</span>
  );
}

// The counters double as the status filter: reading "82 not invited" and then
// hunting for a chip that says the same thing is one step too many.
function Stat({ n, label, tone, on, onClick }) {
  const t = TONE[tone] || TONE.muted;
  return (
    <button type="button" onClick={onClick} style={{
      padding: '10px 16px', borderRadius: 12, minWidth: 118, textAlign: 'start', cursor: 'pointer',
      border: `1px solid ${on ? t.bd : 'var(--border-subtle)'}`,
      background: on ? t.bg : 'var(--bg-raised)', font: 'inherit',
    }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: t.fg }}>{n}</div>
      <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{label}</div>
    </button>
  );
}

function Th({ children, isAr }) {
  return (
    <th style={{
      padding: '10px 12px', fontWeight: 600, fontSize: 12,
      color: 'var(--text-secondary)', textAlign: isAr ? 'right' : 'left', whiteSpace: 'nowrap',
    }}>{children}</th>
  );
}

export default function ConsoleClaimCampaign({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const font = { fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' };

  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState('all');
  const [cat, setCat] = useState('all');
  const [q, setQ] = useState('');

  // Both bulk actions share one dialog shape:
  // null → { kind: 'consent' | 'send', phase: 'preview' | 'done', rows }.
  const [dlg, setDlg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState('');

  // Selection survives a filter change on purpose — an admin who picks rows in
  // one category and then looks at another has not changed their mind. The bulk
  // bar says how many of the picks are currently out of view so it is never a
  // surprise, and the dry run is the final word on what actually goes.
  const [selected, setSelected] = useState(() => new Set());

  // ── Which campaign this tab is driving ──
  // 'invite'   → claim_invite, the first touch, for not_invited factories
  // 'followup' → factory_claim, the second, for invited-and-unclaimed ones
  //
  // The two must never share a selection. Their eligible pools are exact
  // opposites, so a set of ids picked under one rule is not merely useless under
  // the other — it is a list of factories the other campaign should not touch.
  // Switching tabs therefore CLEARS the selection rather than carrying it, which
  // is the one place in this screen where a selection is dropped on purpose
  // (a filter change deliberately keeps it; see the note on `selected`).
  const [campaign, setCampaign] = useState('invite');
  const switchCampaign = (next) => {
    if (next === campaign) return;
    setCampaign(next);
    setSelected(new Set());
    setStatus('all');
  };
  const followUp = campaign === 'followup';
  const selectableOf = followUp ? isFollowUpSelectable : isSelectable;

  const [reloadKey, setReloadKey] = useState(0);

  // The row whose notes are open. Closing reloads the board so note_count
  // catches up with whatever was added or deleted in the thread — the component
  // owns its own writes and has no way to tell us about them.
  const [notesFor, setNotesFor] = useState(null);
  const closeNotes = () => { setNotesFor(null); setReloadKey((k) => k + 1); };

  useEffect(() => {
    let alive = true;
    fetchCampaignRows()
      .then((r) => { if (alive) setRows(r); })
      .catch((e) => { if (alive) { setErr(e.message || 'load failed'); setRows([]); } });
    return () => { alive = false; };
  }, [reloadKey]);

  // Preview first, always — for both actions. The dialog quotes the server's own
  // count, from the same function that will do the writing.
  const runAction = async (kind, phase) => {
    const dryRun = phase === 'preview';
    setActionErr(''); setBusy(true);
    try {
      let rows_;
      if (kind === 'consent') rows_ = await markPriorContactBulk({ dryRun });
      else if (kind === 'followup') rows_ = await sendFactoryClaim([...selected], { dryRun });
      else rows_ = await sendClaimInvites([...selected], { dryRun });
      setDlg({ kind, phase: dryRun ? 'preview' : 'done', rows: rows_ });
      if (!dryRun) {
        setReloadKey((k) => k + 1);
        if (kind === 'send' || kind === 'followup') setSelected(new Set());
      }
    } catch (e) {
      setActionErr(e.message || (dryRun ? 'preview failed' : 'failed'));
      if (dryRun) setDlg(null);
    }
    setBusy(false);
  };

  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const counts = useMemo(() => {
    const c = { all: (rows || []).length };
    CAMPAIGN_STATUSES.forEach((s) => { c[s] = 0; });
    (rows || []).forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
    return c;
  }, [rows]);

  // Filter on the DISPLAY group, not the raw code. Several real codes collapse
  // into one group — furniture, office_furniture, kitchen_furniture and
  // home_decor all read "Furniture & Home/Office Décor" — so filtering by code
  // would put four chips with identical text side by side.
  const groupOf = (code) => displayCategoryForCode(code)?.key || null;
  const catLabel = (code) => {
    const c = displayCategoryForCode(code);
    return c ? (c.label[lang] || c.label.en) : (code || '—');
  };

  // Only groups that actually occur, so the filter never offers a dead chip.
  const categories = useMemo(() => {
    const seen = new Map();
    (rows || []).forEach((r) => {
      const c = displayCategoryForCode(r.category);
      if (c && !seen.has(c.key)) seen.set(c.key, c.label[lang] || c.label.en);
    });
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows, lang]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (rows || []).filter((r) => {
      if (status !== 'all' && r.status !== status) return false;
      if (cat !== 'all' && groupOf(r.category) !== cat) return false;
      if (s && ![r.display_name, r.company_name, r.city].filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, status, cat, q]);

  // Everything the bulk bar needs to stay honest about a selection that may
  // reach beyond what is currently on screen.
  const selectableVisible = useMemo(() => filtered.filter(selectableOf), [filtered, selectableOf]);

  const allVisibleSelected = selectableVisible.length > 0
    && selectableVisible.every((r) => selected.has(r.factory_id));

  const hiddenSelected = useMemo(() => {
    const visible = new Set(filtered.map((r) => r.factory_id));
    return [...selected].filter((id) => !visible.has(id)).length;
  }, [selected, filtered]);

  // "Select all" means every SELECTABLE row in the CURRENT filter — never the
  // whole table, and never a row the sender would reject anyway.
  const toggleAllVisible = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allVisibleSelected) selectableVisible.forEach((r) => next.delete(r.factory_id));
    else selectableVisible.forEach((r) => next.add(r.factory_id));
    return next;
  });

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="claim-campaign">
      <div className="ac-page">
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1" style={font}>{isAr ? 'حملة دعوات المطالبة' : 'Claim Invitation Campaign'}</h1>
            <p className="ac-sub" style={font}>
              {isAr
                ? 'المصانع النشطة فقط. الحالة مشتقّة في قاعدة البيانات — لا تُضبط يدويًا.'
                : 'Active factories only. Status is derived in the database, never set by hand.'}
            </p>
          </div>
          <button type="button" className="ac-btn" style={{ ...font, whiteSpace: 'nowrap' }}
            onClick={() => runAction('consent', 'preview')} disabled={busy || rows === null}>
            {busy && !dlg
              ? '…'
              : isAr ? 'توثيق الموافقة للمصانع ذات الأرقام الصالحة' : 'Document consent for factories with a valid number'}
          </button>
        </div>

        {actionErr && <div className="ac-empty" style={{ ...font, color: TONE.bad.fg }}>{actionErr}</div>}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <Stat n={counts.all} label={isAr ? 'الكل' : 'All'} tone="muted"
            on={status === 'all'} onClick={() => setStatus('all')} />
          {CAMPAIGN_STATUSES.map((s) => (
            <Stat key={s} n={counts[s]} tone={STATUS_LABEL[s].tone}
              label={isAr ? STATUS_LABEL[s].ar : STATUS_LABEL[s].en}
              on={status === s} onClick={() => setStatus(status === s ? 'all' : s)} />
          ))}
        </div>

        {/* ── The two campaigns ──
            Separate tabs rather than two buttons over one list, because the
            eligible pools are exact opposites: the first invite wants
            not_invited, the follow-up wants invited. One list with two buttons
            would show every row greyed out for one of them, and would invite
            the mistake of selecting under one rule and sending under the other.
            Switching clears the selection — see switchCampaign. */}
        <div role="tablist" style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {[
            { key: 'invite', ar: 'الدعوة الأولى', en: 'First invitation' },
            { key: 'followup', ar: 'المتابعة', en: 'Follow-up' },
          ].map((tb) => (
            <button key={tb.key} type="button" role="tab" aria-selected={campaign === tb.key}
              className={`ac-chip${campaign === tb.key ? ' on' : ''}`}
              style={{ ...font, fontWeight: campaign === tb.key ? 700 : 500 }}
              onClick={() => switchCampaign(tb.key)}>
              {isAr ? tb.ar : tb.en}
            </button>
          ))}
          <span style={{ alignSelf: 'center', fontSize: 11.5, color: 'var(--text-tertiary)', ...font }}>
            {followUp
              ? (isAr
                ? 'قالب factory_claim — للمصانع التي وصلتها الدعوة الأولى ولم تطالب بصفحتها.'
                : 'factory_claim — factories that received the first invite and have not claimed.')
              : (isAr
                ? 'قالب claim_invite — للمصانع التي لم تُدعَ بعد.'
                : 'claim_invite — factories that have not been invited yet.')}
          </span>
        </div>

        <div className="ac-chiprow" style={{ marginBottom: 12 }}>
          <button type="button" className={`ac-chip${cat === 'all' ? ' on' : ''}`} style={font}
            onClick={() => setCat('all')}>{isAr ? 'كل الفئات' : 'All categories'}</button>
          {categories.map(([key, label]) => (
            <button key={key} type="button" className={`ac-chip${cat === key ? ' on' : ''}`} style={font}
              onClick={() => setCat(cat === key ? 'all' : key)}>{label}</button>
          ))}
        </div>

        <div style={{ marginBottom: 14 }}>
          <input className="ac-input" style={{ width: '100%', maxWidth: 360, ...font }}
            placeholder={isAr ? 'ابحث باسم المصنع أو المدينة…' : 'Search factory or city…'}
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>

        {selected.size > 0 && (
          <div className="ac-bulkbar" style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            padding: '10px 14px', marginBottom: 12, borderRadius: 10,
            border: `1px solid ${TONE.action.bd}`, background: TONE.action.bg, ...font,
          }}>
            <strong style={{ fontSize: 13 }}>
              {isAr ? `${selected.size} مصنعًا محدّدًا` : `${selected.size} factories selected`}
            </strong>
            {hiddenSelected > 0 && (
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {isAr ? `منها ${hiddenSelected} خارج التصفية الحالية` : `${hiddenSelected} of them outside the current filter`}
              </span>
            )}
            {selected.size > DAILY_SOFT_CAP && (
              <span style={{ fontSize: 12, color: TONE.bad.fg }}>
                {isAr
                  ? `أكثر من ${DAILY_SOFT_CAP} في دفعة واحدة — الرقم يرسل ١٠–٢٠ يوميًا.`
                  : `More than ${DAILY_SOFT_CAP} in one batch — the number sends 10–20 a day.`}
              </span>
            )}
            <span style={{ flex: 1 }} />
            <button type="button" className="ac-btn ac-btn-sm" style={font}
              onClick={() => setSelected(new Set())} disabled={busy}>
              {isAr ? 'إلغاء التحديد' : 'Clear'}
            </button>
            {/* Preview ALWAYS. 'preview' is a dry run on the server, and the
                dialog it opens is the only thing that can reach the real send —
                there is no path from this button to a message. */}
            <button type="button" className="ac-btn ac-btn-sm ac-btn-primary" style={font}
              onClick={() => runAction(followUp ? 'followup' : 'send', 'preview')} disabled={busy}>
              {busy && !dlg ? '…' : followUp
                ? (isAr ? 'معاينة المتابعة' : 'Preview follow-up')
                : (isAr ? 'إرسال الدعوات' : 'Send invitations')}
            </button>
          </div>
        )}

        {err && <div className="ac-empty" style={{ ...font, color: TONE.bad.fg }}>{err}</div>}

        {rows === null ? (
          <div className="ac-empty" style={font}>{isAr ? 'جارٍ التحميل…' : 'Loading…'}</div>
        ) : filtered.length === 0 ? (
          <div className="ac-empty" style={font}>{isAr ? 'لا نتائج.' : 'No results.'}</div>
        ) : (
          <div style={{
            overflowX: 'auto', border: '1px solid var(--border-subtle)',
            borderRadius: 12, background: 'var(--bg-raised)',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, ...font }}>
              <thead>
                <tr style={{ background: 'var(--bg-subtle)' }}>
                  <Th isAr={isAr}>
                    <input type="checkbox" checked={allVisibleSelected}
                      onChange={toggleAllVisible} disabled={selectableVisible.length === 0}
                      title={isAr ? 'تحديد كل القابل للإرسال ضمن التصفية الحالية' : 'Select every sendable row in the current filter'} />
                  </Th>
                  <Th isAr={isAr}>{isAr ? 'المصنع' : 'Factory'}</Th>
                  <Th isAr={isAr}>{isAr ? 'الفئة' : 'Category'}</Th>
                  <Th isAr={isAr}>{isAr ? 'الحالة' : 'Status'}</Th>
                  <Th isAr={isAr}>{isAr ? 'الموافقة' : 'Consent'}</Th>
                  <Th isAr={isAr}>{isAr ? 'تاريخ الدعوة' : 'Invited'}</Th>
                  <Th isAr={isAr}>{isAr ? 'تاريخ المطالبة' : 'Claimed'}</Th>
                  <Th isAr={isAr}> </Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.factory_id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    {/* Non-actionable rows stay VISIBLE but unselectable, with
                        the reason in the title: a hidden row is a row nobody
                        thinks to ask about. */}
                    <td style={tdS}>
                      <input type="checkbox" checked={selected.has(r.factory_id)}
                        disabled={!selectableOf(r)}
                        onChange={() => toggleOne(r.factory_id)}
                        title={selectableOf(r) ? '' : (isAr ? STATUS_LABEL[r.status]?.ar : STATUS_LABEL[r.status]?.en) || ''} />
                    </td>
                    <td style={tdS}>
                      <div style={{ fontWeight: 600 }} dir="ltr">{r.display_name || '—'}</div>
                      {r.city && <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{r.city}</div>}
                    </td>
                    <td style={tdS}>{catLabel(r.category)}</td>
                    <td style={tdS}>
                      <StatusBadge status={r.status} isAr={isAr} />
                      {/* Delivery state exists only once a message was queued. */}
                      {r.invite_status && (
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }} dir="ltr">
                          {r.invite_status}{r.invite_error ? ` · ${r.invite_error}` : ''}
                        </div>
                      )}
                    </td>
                    {/* Consent is what the bulk button changes. Without this
                        column, pressing it would alter nothing visible. */}
                    <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                      {hasConsent(r)
                        ? <span style={{ fontSize: 11.5, color: TONE.ok.fg }} dir="ltr">
                            {r.opt_in_source || 'opted in'}
                          </span>
                        : <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{fmtDate(r.invited_at, isAr) || '—'}</td>
                    <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{fmtDate(r.claimed_at, isAr) || '—'}</td>
                    {/* The public page the invitation points people at. Every
                        row here is is_active, and /factory/:id reads
                        factory_directory_public (= where is_active), so this
                        never lands on a missing page. New tab on purpose: the
                        admin is mid-selection and must not lose it. */}
                    <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                      <a className="ac-btn ac-btn-sm" href={`/factory/${r.factory_id}`}
                        target="_blank" rel="noreferrer" style={{ ...font, textDecoration: 'none' }}
                        title={isAr ? 'فتح صفحة المصنع العامة' : 'Open the public factory page'}>
                        {isAr ? 'الصفحة ↗' : 'Page ↗'}
                      </a>
                      <button type="button" className="ac-btn ac-btn-sm"
                        style={{ ...font, marginInlineStart: 6 }}
                        onClick={() => setNotesFor(r)}
                        title={isAr ? 'ملاحظات إدارية' : 'Admin notes'}>
                        {isAr ? 'ملاحظات' : 'Notes'}
                        {r.note_count > 0 && (
                          <span style={{
                            marginInlineStart: 6, fontSize: 10.5, fontWeight: 700,
                            color: TONE.info.fg, background: TONE.info.bg,
                            border: `1px solid ${TONE.info.bd}`, borderRadius: 999, padding: '1px 6px',
                          }}>{r.note_count}</span>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {notesFor && (
        <div className="ac-modal-ov" onClick={closeNotes}>
          <div className="ac-modal" style={{ maxWidth: 520, ...font }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
              <p className="ac-modal-title" style={{ margin: 0 }} dir="ltr">{notesFor.display_name}</p>
              <button type="button" className="ac-btn ac-btn-sm" style={font} onClick={closeNotes}>
                {isAr ? 'إغلاق' : 'Close'}
              </button>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '0 0 14px' }}>
              {isAr ? 'داخلية — لا يراها المصنع.' : 'Internal — the factory never sees these.'}
            </p>
            {/* The shared admin notes thread, unchanged. It carries its own
                fonts and spacing, which do not quite match the console; forking
                it to restyle would mean two components to keep in step for a
                cosmetic gain. */}
            <AdminNoteThread entityType="factory" entityId={notesFor.factory_id} user={user} lang={lang} />
          </div>
        </div>
      )}

      {dlg && (
        <OutcomeDialog
          kind={dlg.kind} phase={dlg.phase} rows={dlg.rows}
          isAr={isAr} font={font} busy={busy}
          onConfirm={() => runAction(dlg.kind, 'run')} onClose={() => setDlg(null)} />
      )}
    </ConsoleShell>
  );
}

// ── The message preview ─────────────────────────────────────────────────────
// A mirror, never the source: Meta renders the template from its own copy and
// we send only the variables. CLAIM_INVITE_TEMPLATE has to be hand-updated when
// the template changes — see the warning on it.

// WhatsApp renders *text* as bold. The approved body carries no asterisks
// today, but a future edit could add them, and leaving them literal would put
// something on screen the factory never sees.
function waBold(text) {
  return text.split(/(\*[^*]+\*)/g).map((part, i) => (
    part.length > 2 && part.startsWith('*') && part.endsWith('*')
      ? <strong key={i}>{part.slice(1, -1)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  ));
}

// One real example, not a mock-up: the name and the slug both come from the
// server's dry run, and the slug is the same value the send passes as
// button_url_suffix — so the URL shown here cannot drift from the URL sent.
function PhonePreview({ row, template, isAr }) {
  const t = template || CLAIM_INVITE_TEMPLATE;
  const name = row.display_name || '—';

  // ONE mockup for both campaigns — same phone, same chrome, same layout. Only
  // the body text differs, so only the body text is computed here.
  //
  // {{2}} exists only on factory_claim: the count of that factory's products,
  // which the server returns on the dry-run row.
  const bodyText = (t.body || '')
    .replace(/\{\{1\}\}/g, name)
    .replace(/\{\{2\}\}/g, String(row.product_count ?? '—'));
  // Cosmetic only. WhatsApp stamps a receipt time on every message; nothing
  // reads this back and nothing depends on it.
  const now = new Date();
  const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  return (
    /* A phone, the way Meta's and Widers' own template previews show one — so
       this reads as a screen rather than a panel in our console. NOT clipped
       anywhere: the footer and the button must always be visible, which is why
       the dialog lays out in two columns rather than making this scroll.
       dir=ltr because the template is en_US whatever language the admin reads. */
    <div style={{
      width: 260, flex: '0 0 auto', background: WA.bezel, padding: 6,
      borderRadius: 24, border: `1px solid ${WA.frame}`,
      boxShadow: '0 6px 20px rgba(0,0,0,.16)',
    }}>
      <div style={{ borderRadius: 17, overflow: 'hidden' }}>

        {/* Chat header. Decorative — nothing here is clickable. */}
        <div dir="ltr" style={{
          display: 'flex', alignItems: 'center', gap: 9,
          height: 48, padding: '0 10px', background: WA.header,
          fontFamily: WA.font,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff"
            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <div style={{
            width: 30, height: 30, borderRadius: '50%', background: WA.avatar,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 14, fontWeight: 700, flexShrink: 0,
          }}>M</div>
          <div style={{ lineHeight: 1.25, minWidth: 0 }}>
            <div style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>MAABAR</div>
            <div style={{ color: '#fff', opacity: 0.8, fontSize: 11 }}>Business account</div>
          </div>
        </div>

        <div style={{
          backgroundColor: WA.chat, backgroundImage: WA.doodle, padding: 10,
        }}>
        <div dir="ltr" style={{
          position: 'relative', maxWidth: '88%',
          background: WA.bubble, borderRadius: 8, borderTopLeftRadius: 0,
          boxShadow: '0 1px 0.5px rgba(0,0,0,.13)',
          fontFamily: WA.font, textAlign: 'left',
        }}>
          {/* The tail WhatsApp draws on an incoming bubble's top-left corner. */}
          <div style={{
            position: 'absolute', top: 0, left: -8, width: 0, height: 0,
            borderStyle: 'solid', borderWidth: '0 8px 8px 0',
            borderColor: `transparent ${WA.bubble} transparent transparent`,
          }} />

          <div style={{ padding: '7px 9px 6px' }}>
            <div style={{ color: WA.sender, fontWeight: 600, fontSize: 12.5, marginBottom: 3 }}>
              MAABAR
            </div>

            {/* pre-line, not paragraph splitting: the approved body separates
                the two ✅ lines with a SINGLE newline, which HTML would collapse
                to a space. pre-line preserves single and double breaks alike,
                which is how WhatsApp lays the message out. */}
            {/* ⚠️ A template whose approved copy is not mirrored yet shows an
                explicit gap HERE, inside the same bubble — never invented text.
                A plausible message would be worse than none: an admin would
                approve a send having read something Meta does not render, and
                the whole point of this preview is to be what the factory gets.
                The phone, the button and the facts panel stay identical, so the
                only thing missing is the only thing we do not know. */}
            <div style={{
              whiteSpace: 'pre-line', fontSize: 13, lineHeight: 1.35,
              color: t.body ? WA.text : '#9a6b00',
              ...(t.body ? null : {
                background: '#fff6e0', border: '1px dashed #d9a441',
                borderRadius: 6, padding: '7px 8px',
              }),
            }}>
              {t.body ? waBold(bodyText) : (isAr
                ? 'نصّ هذا القالب غير منسوخ في المستودع بعد. المتغيّرات والزر أدناه حقيقية ومن الخادم — أمّا النص فيُقرأ من لوحة Widers قبل الإرسال. لا يُعرض هنا نصّ مُختلَق.'
                : 'This template’s approved text is not mirrored in the repo yet. The variables and the button below are real and come from the server — the text itself must be read from the Widers console before sending. No invented copy is shown here.')}
            </div>

            {t.footer && (
              <div style={{ marginTop: 6, fontSize: 11, color: WA.footer }}>{t.footer}</div>
            )}

            <div style={{ marginTop: 3, textAlign: 'right', fontSize: 11, color: WA.stamp }}>
              {time}
            </div>
          </div>

          <div style={{
            borderTop: `1px solid ${WA.divider}`, textAlign: 'center',
            padding: 8, color: WA.action, fontSize: 13, fontWeight: 600,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true"
              style={{ verticalAlign: '-2px', marginRight: 6 }}>
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            {t.buttonLabel}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

// What the phone cannot say for itself: which factory this example is, which
// locale actually goes out, where the button lands, and what the batch costs.
function PreviewFacts({ row, count, isAr, template }) {
  const t = template || CLAIM_INVITE_TEMPLATE;
  const name = row.display_name || '—';
  const url = `${t.urlBase}${row.claim_slug || ''}`;
  const cost = (count * MARKETING_RATE_SAR).toFixed(2);

  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        gap: 8, marginBottom: 6, fontSize: 11.5, color: 'var(--text-tertiary)',
      }}>
        <span>
          {isAr ? 'مثال: ' : 'Example: '}
          <span dir="ltr" style={{ fontWeight: 600 }}>{name}</span>
          {/* factory_claim's {{2}}. Shown because it is a variable the admin is
              approving, not decoration — a wrong count is a wrong message. */}
          {row.product_count != null && (
            <span style={{ color: 'var(--text-secondary)' }}>
              {isAr ? ` · ${row.product_count} منتجًا` : ` · ${row.product_count} products`}
            </span>
          )}
        </span>
        <span dir="ltr">{t.lang}</span>
      </div>

      {/* The resolved destination. Meta builds it from the template's base plus
          the suffix; this is that same concatenation, shown in full. */}
      <div dir="ltr" style={{
        fontSize: 11, color: 'var(--text-tertiary)',
        wordBreak: 'break-all', textAlign: 'left',
      }}>{url}</div>

      <div style={{ margin: '8px 0 14px', fontSize: 11.5, color: 'var(--text-secondary)' }}>
        {isAr
          ? `التكلفة التقديرية: ${cost} ر.س (${count} × ${MARKETING_RATE_SAR})`
          : `Estimated cost: ${cost} SAR (${count} × ${MARKETING_RATE_SAR})`}
      </div>
    </>
  );
}

// Preview → confirm → result, over the SAME server function for both actions.
// The count shown before the write and the count reported after it come from
// one code path, so the dialog cannot promise something the execution then
// contradicts. `kind` only swaps the vocabulary and the copy.
function OutcomeDialog({ kind, phase, rows, isAr, font, busy, onConfirm, onClose }) {
  // `send` means "this action puts WhatsApp messages in the queue", which both
  // campaigns do; `follow` distinguishes WHICH template, and so which
  // vocabulary and which preview.
  const follow = kind === 'followup';
  const send = kind === 'send' || follow;
  const table = follow ? FOLLOW_UP_OUTCOME_LABEL : (send ? SEND_OUTCOME_LABEL : OUTCOME_LABEL);
  const template = follow ? FACTORY_CLAIM_TEMPLATE : CLAIM_INVITE_TEMPLATE;
  const goKey = send ? 'enqueued' : 'marked';

  const counts = summarizeOutcomes(rows);
  const willGo = counts[goKey] || 0;
  const skipped = (rows || []).length - willGo;
  const preview = phase === 'preview';

  // The first factory that will actually be ENQUEUED, not merely the first
  // selected: previewing a row about to be skipped would show a message that
  // factory never receives.
  const example = send && preview ? (rows || []).find((r) => r.outcome === 'enqueued') : null;

  const title = follow
    ? (preview ? (isAr ? 'إرسال المتابعة' : 'Send follow-up') : (isAr ? 'أُدرجت في الطابور' : 'Queued'))
    : send
      ? (preview ? (isAr ? 'إرسال الدعوات' : 'Send invitations') : (isAr ? 'أُدرجت في الطابور' : 'Queued'))
      : (preview ? (isAr ? 'توثيق الموافقة' : 'Document consent') : (isAr ? 'تمّ التوثيق' : 'Consent recorded'));

  const body = send
    ? (preview
      ? (isAr
        ? `${willGo} مصنعًا ستُدرَج رسالته في الطابور، و${skipped} متخطّى. يعمل المصرِّف كل دقيقة، فتخرج بعد التأكيد بوقت قصير.`
        : `${willGo} factories will be queued, ${skipped} skipped. The drain runs every minute, so these leave shortly after you confirm.`)
      : (isAr
        ? `أُدرجت ${willGo} رسالة في الطابور. يعمل المصرِّف كل دقيقة — وهي في طريقها الآن.`
        : `${willGo} messages queued. The drain runs every minute — they are on their way.`))
    : (preview
      ? (isAr
        ? `سيُسجَّل أساس التواصل السابق لـ ${willGo} مصنعًا. هذا يفتح قناة الواتساب لهم — لا يُرسل رسالة الآن، لكنه ليس إجراءً بلا أثر.`
        : `Records the prior-contact basis for ${willGo} factories. This opens the WhatsApp channel for them — it sends nothing now, but it is not a no-op.`)
      : (isAr
        ? `سُجِّلت الموافقة لـ ${willGo} مصنعًا.`
        : `Consent recorded for ${willGo} factories.`));

  // Extracted so the one- and two-column layouts render the SAME nodes rather
  // than two copies that could drift apart. Declared after `body`, which intro
  // reads.
  const intro = (
    <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8, margin: '0 0 14px' }}>
      {body}
    </p>
  );

  const breakdown = (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
      {Object.keys(table).filter((k) => counts[k]).map((k, i) => (
        <div key={k} style={{
          display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 12px',
          fontSize: 12.5, borderTop: i ? '1px solid var(--border-subtle)' : 'none',
          background: k === goKey ? TONE.ok.bg : 'transparent',
        }}>
          <span style={{ color: 'var(--text-secondary)' }}>
            {outcomeLabel(k, isAr, !preview, table)}
          </span>
          <strong>{counts[k]}</strong>
        </div>
      ))}
    </div>
  );

  // Extracted so the two-column layout can put them at the foot of the right
  // column, where they sit beside the phone instead of below it.
  const actions = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
      <button type="button" className="ac-btn" style={font} onClick={onClose} disabled={busy}>
        {preview ? (isAr ? 'إلغاء' : 'Cancel') : (isAr ? 'إغلاق' : 'Close')}
      </button>
      {preview && (
        <button type="button" className="ac-btn ac-btn-primary" style={font}
          onClick={onConfirm} disabled={busy || willGo === 0}>
          {busy ? '…' : send
            ? (isAr ? `إرسال ${willGo}` : `Send ${willGo}`)
            : (isAr ? `توثيق ${willGo}` : `Document ${willGo}`)}
        </button>
      )}
    </div>
  );

  return (
    <div className="ac-modal-ov" onClick={onClose}>
      {/* Two columns ONLY when the phone is on screen: it is too tall to stack
          above the breakdown without pushing the Send button out of view. The
          consent dialog and the post-send state keep the narrow single column.
          85vh + overflow stays as a floor for short windows — with two columns
          it should not trigger at normal desktop heights. */}
      <div className="ac-modal" style={{
        maxWidth: example ? 'min(800px, 92vw)' : 460,
        // .ac-modal is padding: 22px (console.css:262). Trimmed inline for the
        // two-column case only — the shared class is untouched.
        padding: example ? 16 : undefined,
        maxHeight: '85vh', overflowY: 'auto', ...font,
      }}
        onClick={(e) => e.stopPropagation()}>
        <p className="ac-modal-title" style={{ margin: '0 0 6px' }}>{title}</p>

        {/* Every reason stays itemised, including the skips — a summary showing
            only the successes would hide exactly what needs explaining. */}
        {example ? (
          // flexWrap + a 340px basis: below roughly 860px the right column drops
          // beneath the phone rather than squeezing it, and the layout degrades
          // to the single column it had before.
          <div style={{
            display: 'flex', gap: 20, flexWrap: 'wrap',
            alignItems: 'flex-start', justifyContent: 'center',
          }}>
            <PhonePreview row={example} template={template} isAr={isAr} />
            <div style={{ flex: '1 1 340px', minWidth: 0 }}>
              {intro}
              <PreviewFacts row={example} count={willGo} isAr={isAr} template={template} />
              {breakdown}
              {actions}
            </div>
          </div>
        ) : (
          <>
            {intro}
            {breakdown}
            {actions}
          </>
        )}
      </div>
    </div>
  );
}
